// Agreements — a second business's people read and sign agreements on their own site (tenant
// module `agreements`; backend netlify/functions/agreements-sb.js). The text arrives as blocks
// parsed on the server, the same blocks the signed PDF is drawn from, so what is read here is
// word for word what is signed. Tenant-only: imports nothing from the Hollypaul engine.
import React, { useState, useEffect, useCallback } from 'react';
import { FileSignature, CheckCircle, Clock, Download, Loader, Lock, AlertTriangle } from 'lucide-react';
import { SignaturePad } from '../components/SignaturePad';
import { BUSINESS_NAME } from '../tenant/config';

const API = '/.netlify/functions/agreements-sb';
const post = (body) => fetch(API, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) }).then((r) => r.json());
const when = (iso) => (iso ? new Date(iso).toLocaleString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

function Inline({ segs }) {
  return (segs || []).map((s, i) => (s.b ? <strong key={i}>{s.t}</strong> : s.i ? <em key={i}>{s.t}</em> : <React.Fragment key={i}>{s.t}</React.Fragment>));
}

function Body({ blocks }) {
  return (
    <div style={{ fontSize: '0.92rem', lineHeight: 1.6, color: 'var(--hp-text)' }}>
      {blocks.map((b, i) => {
        if (b.type === 'h1') return <h2 key={i} style={{ margin: '18px 0 8px' }}><Inline segs={b.text} /></h2>;
        if (b.type === 'h2' || b.type === 'h3') return <h3 key={i} style={{ margin: '20px 0 6px', fontSize: '1rem' }}><Inline segs={b.text} /></h3>;
        if (b.type === 'p') return <p key={i} style={{ margin: '0 0 10px' }}><Inline segs={b.text} /></p>;
        if (b.type === 'ul' || b.type === 'ol') {
          const Tag = b.type;
          return (
            <Tag key={i} style={{ margin: '0 0 10px', paddingLeft: 22 }}>
              {b.items.map((it, j) => <li key={j} style={{ marginLeft: it.level * 18, listStyleType: it.level ? 'circle' : undefined, marginBottom: 4 }}><Inline segs={it.text} /></li>)}
            </Tag>
          );
        }
        if (b.type === 'table') {
          return (
            <div key={i} style={{ overflowX: 'auto', margin: '0 0 12px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <tbody>
                  {b.rows.map((r, ri) => (
                    <tr key={ri}>
                      {r.map((c, ci) => {
                        const Cell = ri === 0 ? 'th' : 'td';
                        return <Cell key={ci} style={{ textAlign: 'left', verticalAlign: 'top', padding: '7px 9px', borderBottom: '1px solid var(--hp-border)' }}><Inline segs={c} /></Cell>;
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        return null;
      })}
    </div>
  );
}

function SignForm({ agreement, me, onSigned }) {
  const signer = agreement.signers.find((s) => s.login === me);
  const [fullName, setFullName] = useState('');
  const [address, setAddress] = useState('');
  const [position, setPosition] = useState(signer && signer.positions ? '' : 'Personally');
  const [signature, setSignature] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const field = { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid var(--hp-border)', background: 'var(--hp-card)', color: 'var(--hp-text)', fontSize: '0.95rem', boxSizing: 'border-box' };

  const submit = async () => {
    setBusy(true); setErr('');
    try {
      const d = await post({ action: 'sign', id: agreement.id, bodyHash: agreement.bodyHash, fullName, address, position, signature, agree });
      if (!d.success) throw new Error(d.error || 'Signing failed');
      onSigned(d.agreement);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="card" style={{ padding: 20, marginTop: 18, border: '1px solid var(--hp-gold)' }}>
      <h3 style={{ margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}><FileSignature size={18} /> Sign this agreement</h3>
      <p style={{ margin: '0 0 14px', fontSize: '0.82rem', color: 'var(--hp-dim)' }}>You are signing as: {signer ? signer.label : me}</p>
      <div className="form-group" style={{ marginBottom: 12 }}>
        <label className="form-label">Your full legal name</label>
        <input value={fullName} onChange={(e) => setFullName(e.target.value)} style={field} autoComplete="name" />
      </div>
      <div className="form-group" style={{ marginBottom: 12 }}>
        <label className="form-label">Your address</label>
        <input value={address} onChange={(e) => setAddress(e.target.value)} style={field} autoComplete="street-address" />
      </div>
      {signer && signer.positions && (
        <div className="form-group" style={{ marginBottom: 12 }}>
          <label className="form-label">Your position at {BUSINESS_NAME}</label>
          <select value={position} onChange={(e) => setPosition(e.target.value)} style={field}>
            <option value="">Choose…</option>
            {signer.positions.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      )}
      <div className="form-group" style={{ marginBottom: 12 }}>
        <label className="form-label">Signature</label>
        <SignaturePad onChange={setSignature} defaultName={fullName} />
      </div>
      <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: '0.88rem', margin: '14px 0', cursor: 'pointer' }}>
        <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
        <span>I have read the whole agreement above and I agree to it, for myself{signer && signer.positions ? ' and on behalf of the company' : ''}. I understand this electronic signature is as binding as a handwritten one.</span>
      </label>
      {err && <div style={{ color: 'var(--hp-red)', fontSize: '0.85rem', marginBottom: 10, display: 'flex', gap: 6 }}><AlertTriangle size={15} /> {err}</div>}
      <button className="btn btn-primary" disabled={busy || !agree || !signature || !fullName.trim() || !address.trim() || !position} onClick={submit}>
        {busy ? <><Loader size={14} /> Signing…</> : <><Lock size={14} /> Sign agreement</>}
      </button>
    </div>
  );
}

function Signers({ a }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, margin: '10px 0 0' }}>
      {a.signers.map((s) => (
        <div key={s.login} style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'flex-start', gap: 8, fontSize: '0.85rem' }}>
          <span style={{ flexShrink: 0, marginTop: 2 }}>{s.signed ? <CheckCircle size={15} color="var(--hp-green, #22c55e)" /> : <Clock size={15} color="var(--hp-dim)" />}</span>
          <span>{s.label} <span style={{ color: 'var(--hp-dim)' }}>· {s.signed ? `signed by ${s.fullName}, ${when(s.signedAt)}` : 'not signed yet'}</span></span>
        </div>
      ))}
    </div>
  );
}

export default function AgreementsPage() {
  const [items, setItems] = useState(null);
  const [me, setMe] = useState('');
  const [openId, setOpenId] = useState('');
  const [err, setErr] = useState('');
  const [dl, setDl] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await post({ action: 'list' });
      if (!d.success) throw new Error(d.error || 'Could not load agreements');
      setItems(d.items); setMe(d.me);
      setOpenId((cur) => cur || (d.items.find((a) => a.mine === 'to-sign') || d.items[0] || {}).id || '');
    } catch (e) { setErr(e.message); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const download = async (a) => {
    setDl(a.id);
    try {
      const d = await post({ action: 'pdf', id: a.id });
      if (!d.success) throw new Error(d.error);
      const bytes = Uint8Array.from(atob(d.base64), (c) => c.charCodeAt(0));
      const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
      const link = document.createElement('a'); link.href = url; link.download = d.filename; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { setErr(e.message); } finally { setDl(''); }
  };

  const onSigned = (a) => setItems((list) => list.map((x) => (x.id === a.id ? a : x)));

  if (items === null) return <div className="page-body"><Loader size={16} /> Loading…</div>;
  const a = items.find((x) => x.id === openId);

  return (
    <>
      <div className="page-header"><div className="page-header-left"><h2>Agreements</h2><p>Read and sign the agreements between you and the people you work with.</p></div></div>
      <div className="page-body" style={{ maxWidth: 860 }}>
        {err && <div style={{ color: 'var(--hp-red)', marginBottom: 12 }}>{err}</div>}
        {!items.length && <p style={{ color: 'var(--hp-dim)' }}>No agreements yet.</p>}
        {items.length > 1 && (
          <div className="tabs" style={{ marginBottom: 14 }}>
            {items.map((x) => <button key={x.id} className={`tab${x.id === openId ? ' active' : ''}`} onClick={() => setOpenId(x.id)}>{x.title}</button>)}
          </div>
        )}
        {a && (
          <div className="card" style={{ padding: 22 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', alignItems: 'flex-start' }}>
              <div>
                <h2 style={{ margin: 0 }}>{a.title}</h2>
                <div style={{ fontSize: '0.8rem', color: 'var(--hp-dim)', marginTop: 4 }}>
                  {a.status === 'complete' ? `Signed by everyone · completed ${when(a.completedAt)}` : `Waiting for ${a.signers.filter((s) => !s.signed).length} of ${a.signers.length} signatures`}
                </div>
              </div>
              {a.status === 'complete' && (
                <button className="btn btn-secondary" onClick={() => download(a)} disabled={dl === a.id}>
                  {dl === a.id ? <Loader size={14} /> : <Download size={14} />} Download signed PDF
                </button>
              )}
            </div>
            <Signers a={a} />
            {a.mine === 'signed' && a.status !== 'complete' && (
              <p style={{ fontSize: '0.85rem', color: 'var(--hp-dim)', margin: '10px 0 0' }}>You've signed. The signed PDF will be here once everyone has.</p>
            )}
            <hr style={{ border: 0, borderTop: '1px solid var(--hp-border)', margin: '18px 0' }} />
            <Body blocks={a.blocks} />
            {a.mine === 'to-sign' && a.status === 'open' && <SignForm agreement={a} me={me} onSigned={onSigned} />}
          </div>
        )}
      </div>
    </>
  );
}
